import React, { Component } from "reactn";
import styled from "styled-components";
import sanitizeHtml from "sanitize-html";

import {
    Button,
    Icon,
    FormLabel,
    Checkbox,
    Tooltip,
    CardActions,
    CircularProgress
} from "@material-ui/core";
import ContentEditable from "js/react/components/ContentEditable";
import getLogger, { LogGroup } from "js/core/logger";
import { app } from "js/namespaces";
import { $ } from "js/vendor";
import { themeColors } from "js/react/sharedStyles";
import theme from "js/react/materialThemeOverrides";
import { ds } from "js/core/models/dataService";
import { AssignCollaboratorOrTeammateDropdown } from "js/editor/components/AssignCollaboratorOrTeammateDropdown";
import { ShowDialogAsync, ShowConfirmationDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { ShareDialog } from "js/react/views/PresentationSettings/dialogs/ShareDialog";
import { Key } from "js/core/utilities/keys";
import { trackActivity } from "js/core/utilities/utilities";
import NotificationsService from "js/core/services/notifications";
import InviteEditCollaboratorDialog from "js/react/components/Dialogs/InviteEditCollaboratorDialog";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { isCommentTextEmpty } from "./commentUtils";

const logger = getLogger(LogGroup.COMMENTS);

const MAX_COMMENT_TEXT_LENGTH = 1500;

export const EditContentEditable = styled(ContentEditable)`
    width: 100%;
    height: 100%;
    font-size: 13px;
    font-family: "Source Sans Pro";
    letter-spacing: 0;
    padding: 0 !important;
    margin: 0 !important;
    border: none;
    background: transparent;
    resize: none;
    overflow: hidden;
    outline: none;
    user-select: text;
    word-break: break-word;
    cursor: ${props => props.disabled ? "default" : "text"};
    color: ${props => props.$isViewer ? "#fff" : "#222"};

   :empty:before {
        pointer-events: none;
        display: block;
        font-style: italic;
        color: #888;
        opacity: 1;
        content: attr(data-placeholder);
    }

    ::selection{
        background: ${themeColors.textSelection};
    }
    ::-moz-selection{
        background: ${themeColors.textSelection};
    }

    * {
      &::selection{
        background: ${themeColors.textSelection};
      }
      &::-moz-selection{
        background: ${themeColors.textSelection};
      }
    }
`;

const ComposerBox = styled.div`
    display: flex;
    padding: 10px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    align-self: stretch;
    margin-bottom: 10px;
    border-radius: 5px;
    border: 1px solid transparent;
    background: ${props => props.$isViewer ? "#666666" : "#fff"};
    border-color: ${props => {
        if (props.$isFocused) {
            return "#11A9E2";
        }
        return props.$isViewer ? "transparent" : "#E4E4E4";
    }};
`;

// Hugs the text vertically - the box grows a line at a time instead of reserving empty rows.
// The min-height is one line so the empty field still reads as a click target for the placeholder.
const ComposerInputArea = styled.div`
    flex: 1;
    width: 100%;
    min-height: 18px;
    display: flex;
`;

const SendRow = styled.div`
    align-self: stretch;
    display: flex;
    justify-content: flex-end;
    margin-top: auto;
`;

const SendButton = styled(Icon)`
    &&& {
        cursor: pointer;
        font-size: 20px;
        color: #11A9E2;

        &:hover {
            color: #29b2e5;
        }
    }
`;

const ComposerActions = styled(CardActions)`
    align-self: stretch;
    padding: 0 !important;
    justify-content: flex-end;
    margin-top: auto;
`;

const CommentButton = styled(Button)`
    && {
        color: ${props => props.$isViewer ? "#fff" : "#666666"};
    }
`;

const PostButton = styled(CommentButton)`
    && {
        color: ${props => props.$isViewer ? "#fff" : theme.palette.primary[500]} !important;
    }
`;

const CommentFormLabel = styled(FormLabel)`
    &&& {
        color: #666666;
        font-size: 12px;
        text-transform: none;
        word-break: break-word;
    }
`;

const AssignToSlideDivider = styled.hr`
    margin-top: 15px;
`;

const AssignToSlideContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-bottom: -10px;
`;

const AssignToSlideCheckbox = styled(Checkbox)`
    &&& {
        margin-left: -11px !important;
    }
`;

function sanitizeCommentHtmlText(html) {
    return sanitizeHtml(html, {
        allowedTags: ["br", "font"],
        allowedAttributes: {
            "font": ["data-uid", "data-pending-email", "data-inner-text", "class"]
        }
    })
        // By some reason self-closing br tags break contenteditable
        // when there's a blank line
        .replace(/<br\s*\/>/g, "<br>");
}

function stringToHex(string) {
    return Array.from(new TextEncoder().encode(string))
        .map(byte => byte.toString(16).padStart(2, "0"))
        .join("");
}

/**
 * A self-contained comment/reply composer: a bordered box with an @mention-aware
 * contenteditable and a submit affordance. Owns its own draft text so it can be used
 * for the new comment, a reply, and editing without sharing state.
 *
 * submitVariant:
 *   "icon"    - send icon at the bottom-right (new comment / reply); "Cancel removed" behavior
 *   "buttons" - Cancel + submit text buttons (editing)
 */
class CommentComposer extends Component {
    constructor(props) {
        super(props);

        let text = props.initialText || "";
        // Migrating text to html
        if (text.includes("\n")) {
            text = text.replace(/\n/g, "<br>");
        }

        this.state = {
            text,
            taggedUserAnchorEl: null,
            search: "",
            isAssignUserToSlideSelected: false,
            isAssignSlideToMeSelected: false,
            isShowingDialog: false,
            isFocused: false,
            isSubmitting: false
        };

        this.textRef = React.createRef();

        this.currentSelectionPosition = { start: null, end: null };
    }

    componentDidMount() {
        this._isMounted = true;
        if (this.props.autoFocus) {
            this.textRef.current?.focus();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    /**
     * Returns a LIST of tagged users (including pending ones)
     */
    getTaggedUsers = () => {
        const { collaborators, teammates } = this.props;
        const { text } = this.state;

        const taggedUsers = [];
        [...collaborators, ...teammates]
            .forEach(user => {
                const indexInText = text.indexOf(user.isPending ? `data-pending-email="${user.email}"` : `data-uid="${user.uid}"`);
                if (indexInText < 0) {
                    return;
                }

                if (!taggedUsers.some(taggedUser => taggedUser.uid === user.uid)) {
                    taggedUsers.push({ ...user, indexInText });
                }
            });

        // Sorting so the first tagged user has the first index
        taggedUsers.sort((a, b) => a.indexInText - b.indexInText);

        return taggedUsers;
    }

    /**
     * Returns current selection object and makes sure there's a selection
     * and it belongs to the comment text element
     */
    getSelection = () => {
        const selection = window.getSelection();
        if (selection.type === "None") {
            return null;
        }

        // Check if selection is within the comment
        if (!$(this.textRef.current).has(selection.anchorNode)) {
            return null;
        }

        return selection;
    }

    /**
     * Returns start and end offsets of the current selection in relation
     * to the comment text element
     */
    getSelectionPosition = () => {
        const selectionState = {
            start: null,
            end: null
        };

        const selection = this.getSelection();
        if (!selection) {
            return selectionState;
        }

        const range = selection.getRangeAt(0);
        const selectedLength = range.toString().length;
        const preCaretRange = document.createRange();
        preCaretRange.selectNodeContents(this.textRef.current);
        preCaretRange.setEnd(range.endContainer, range.endOffset);

        selectionState.start = preCaretRange.toString().length - selectedLength;
        selectionState.end = preCaretRange.toString().length;

        return selectionState;
    }

    /**
     * Sets the selection to the supplied position
     */
    setSelectionPosition = (start, end) => {
        const selection = window.getSelection();
        selection.selectAllChildren(this.textRef.current);
        selection.collapseToStart();
        let selectionLength = end - start;
        while (start > 0) {
            selection.modify("move", "right", "character");
            start--;
        }
        while (selectionLength > 0) {
            selection.modify("extend", "right", "character");
            selectionLength--;
        }
    }

    /**
     * Selects the word before caret, returns true if a word was selected
     * WARNING: if the word has leading space or linebreak, it will be selected
     * as well
     */
    selectLastWord = () => {
        const selection = this.getSelection();
        if (selection && selection.isCollapsed) {
            let prevSelectionText;
            let currentSelectionText;
            while (true) {
                prevSelectionText = currentSelectionText;
                selection.modify("extend", "left", "character");
                currentSelectionText = selection.toString();

                // Caret is on a space or a line break
                if (/[\s\n]$/.test(currentSelectionText)) {
                    // Restore the selection
                    selection.collapseToEnd();
                    return false;
                }

                // Reached a space or a line break hence the beginning of the word
                if (/^[\s\n]/.test(currentSelectionText)) {
                    return true;
                }

                // Reached the beginning of the container
                if (currentSelectionText === prevSelectionText) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Returns the selected node (either a font node or the comment text element)
     */
    getSelectedNode = () => {
        const selection = this.getSelection();
        if (!selection) {
            return null;
        }

        let selectedNode = selection.getRangeAt(0).commonAncestorContainer;
        while (selectedNode.tagName.toLowerCase() !== "font" && selectedNode !== this.textRef.current) {
            selectedNode = selectedNode.parentElement;
        }

        return selectedNode;
    }

    getMeUser = () => {
        const authUser = app.user.getAuthUser();
        return {
            uid: authUser.uid,
            email: authUser.email,
            displayName: authUser.displayName,
            photoURL: authUser.photoURL,
            isPending: false
        };
    }

    // Shared comment-context analytics dimensions for the Collab events fired from the
    // composer (Mention / Assign), matching what CommentsPane attaches to comment events.
    // User ID, plan type, user role and workspace are attached globally by the analytics layer.
    collabEventProps = () => {
        const { presentation, byViewer, surface } = this.props;
        return {
            ...(presentation ? presentation.getAnalytics() : {}),
            comment_type: byViewer ? "viewer" : "internal",
            location: surface,
            comment_scope: "slide",
        };
    }

    assignUserToSlide = async user => {
        const { currentSlide, presentation } = this.props;

        if (!user) {
            return false;
        }

        const slide = currentSlide;

        const assignUser = () => {
            if (user.isPending && slide.get("assignedPendingUser") !== user.email) {
                slide.update({ assignedUser: null, assignedPendingUser: user.email, pendingUserAssignedBy: app.user.id });
            } else if (slide.get("assignedUser") !== user.uid) {
                slide.update({ assignedUser: user.uid, assignedPendingUser: null, pendingUserAssignedBy: null });
                // Notifying the assignee (no need to notify yourself when self-assigning)
                if (user.uid !== app.user.id) {
                    NotificationsService.notifyOnUserAssignedToSlide(user.uid, presentation.id, slide.id)
                        .catch(err => logger.error(err, "NotificationsService.notifyOnUserAssignedToSlide() failed", { uid: user?.uid, presentationId: presentation?.id, slideId: slide?.id }));
                }
            }
            const props = {
                ...this.collabEventProps(),
                slide_id: slide.id,
                email: user.email,
                user_id: user.uid || null
            };
            trackActivity("Collab", "Assign", null, null, props, { audit: true });
        };

        if (slide.isLibrarySlide()) {
            return new Promise(resolve => ShowConfirmationDialog({
                title: "Assigning user will save this Team Slide, are you sure?",
                message: "Changes will be applied to all presentations that use this slide.",
                cancelButtonLabel: "Cancel",
                acceptCallback: () => {
                    assignUser();
                    resolve(true);
                },
                cancelCallback: () => {
                    resolve(false);
                }
            }));
        }

        assignUser();
        return true;
    }

    handleClickAssignUser = () => {
        const { currentSlide } = this.props;

        const setIsAssignUserToSlideSelected = () => {
            const { isAssignUserToSlideSelected } = this.state;
            this.setState({ isAssignUserToSlideSelected: !isAssignUserToSlideSelected });
        };

        if (currentSlide.isLibrarySlide()) {
            ShowErrorDialog({ title: "Unable to assign user to this slide", message: "This is shared team slide and can not be assigned to a user" });
        } else {
            setIsAssignUserToSlideSelected();
        }
    }

    closeCollaboratorDropdown = () => {
        this.setState({ taggedUserAnchorEl: null });
    }

    showDialog(...args) {
        this.setState({ isShowingDialog: true });

        return ShowDialogAsync(...args)
            .then(dialogResult => {
                this.setState({ isShowingDialog: false });
                return dialogResult;
            });
    }

    handleTagCollaborator = user => {
        const { presentation } = this.props;

        if (user.permissionType === "view") {
            this.showDialog(InviteEditCollaboratorDialog, {
                presentationId: presentation.id,
                title: `Would you like to give ${user.displayName || user.email} edit permission?`,
                message: `This collaborator currently has view only access. Only collaborators with edit permission can comment on presentations.`,
                email: user.email
            }).then(dialogResult => {
                if (dialogResult === true) {
                    this.closeCollaboratorDropdown();
                    this.updateTextWithTaggedUser(user);
                }
            });
        } else {
            // If collaborator with edit permission then just tag them
            this.closeCollaboratorDropdown();
            this.updateTextWithTaggedUser(user);
        }
    }

    handleTagTeammate = user => {
        const { presentation } = this.props;

        this.showDialog(InviteEditCollaboratorDialog, {
            presentationId: presentation.id,
            title: `Would you like to invite ${user.displayName || user.email} to collaborate on and edit this presentation?`,
            message: `Only collaborators with edit permission can comment on presentations.`,
            email: user.email
        }).then(dialogResult => {
            if (dialogResult === true) {
                this.closeCollaboratorDropdown();
                this.updateTextWithTaggedUser(user);
            }
        });
    }

    /**
     * Adds a font node with the tagged user
     */
    updateTextWithTaggedUser = user => {
        this.setSelectionPosition(this.currentSelectionPosition.start, this.currentSelectionPosition.end);

        if (this.selectLastWord()) {
            const innerText = `@${user.displayName || user.email}`
                // Will collapse multiple spaces to allow correct matching
                // of the font node's innerText with the data-inner-text value
                // (multiple spaces are collapsed when rendered so node.innerText
                // will always have single spaces)
                .replace(/\s+/g, " ");
            // data-inner-text will be used to figuring our if the inner text of the font element is changed
            let html = `<font ${user.isPending ? `data-pending-email=${user.email}` : `data-uid="${user.uid}"`} data-inner-text="${innerText}" class="comment-text-hilited">${innerText}</font>&nbsp;`;
            const selectionText = this.getSelection().toString();
            // If starts with a line break or space, add it to the new html
            if (!selectionText.startsWith("@")) {
                html = `${selectionText[0]}${html}`;
            }
            document.execCommand("insertHTML", false, html);
        }
    }

    handleTagSelect = item => {
        let {
            collaborators,
            teammates,
        } = this.props;
        let user = collaborators.find(x => x.email === item.email);
        if (user) {
            this.handleTagCollaborator(user);
            return;
        }
        user = teammates.find(x => x.uid === item.id);
        if (!user) {
            user = {
                uid: item.uid || item.id,
                email: item.email,
                isPending: true,
            };
        }
        this.handleTagTeammate(user);
    }

    handleAddCollaborator = async (email = "", onInviteSuccess = null) => {
        const { presentation } = this.props;

        if (await this.showDialog(ShareDialog, {
            emails: [this.state.search],
            presentation: presentation,
            selectedPanel: "collaborate",
            location: "mention"
        })) {
            onInviteSuccess && onInviteSuccess();
        }
    }

    handleTextChange = event => {
        const text = sanitizeCommentHtmlText(event.target.value);

        const newState = {
            text,
            taggedUserAnchorEl: null,
            search: ""
        };

        // Will just strip the text if it exceeds the max length after the change,
        // stripped/malformed html nodes will be removed by sanitizeCommentHtmlText()
        if (text.length > MAX_COMMENT_TEXT_LENGTH) {
            newState.text = sanitizeCommentHtmlText(text.slice(0, MAX_COMMENT_TEXT_LENGTH));
            this.setState(newState);
            return;
        }

        // This logic removes font tags for tagged users which text was changed
        // i.e. when you backspace to a tagged user
        let shouldUpdateText = false;
        // This callback will be run after the setState() call
        let postSetStateCallback = () => { };
        const { start, end } = this.getSelectionPosition();
        Array.from(this.textRef.current.querySelectorAll("font"))
            .forEach(fontNode => {
                // data-inner-text contains the desired value of the element's inner text,
                // if it doesn't match => it was changed
                if (fontNode.getAttribute("data-inner-text") !== fontNode.innerText) {
                    fontNode.replaceWith(fontNode.innerText);
                    shouldUpdateText = true;
                }
            });
        if (shouldUpdateText) {
            if (start && end) {
                postSetStateCallback = () => {
                    // Will be restoring the selection state after refresh
                    this.setSelectionPosition(start, end);
                };
            }

            newState.text = this.textRef.current.innerHTML;
        }

        // Normalize a visually-empty field (stray <br>, &nbsp;, whitespace) to "" so the
        // placeholder shows again and the field no longer looks focused after blur
        if (isCommentTextEmpty(newState.text)) {
            newState.text = "";
        }

        // Don't bother with searching for collaborators if we're in viewer mode
        if (!this.props.byViewer) {
            if (this.selectLastWord()) {
                const selection = this.getSelection();
                // trim() because selectLastWord() also selects the leading space or linebreak
                const selectionText = selection.toString().trim();
                // Restore the selection
                selection.collapseToEnd();

                if (selectionText.startsWith("@")) {
                    newState.taggedUserAnchorEl = this.textRef.current;
                    // Will be using for restoring selection position
                    this.currentSelectionPosition = { start, end };
                    if (selectionText.length > 1) {
                        // slice() to remove the leading @
                        newState.search = selectionText.slice(1).toLowerCase();
                    }
                }
            }
        }

        this.setState(newState, postSetStateCallback);
    }

    handleKeyDown = event => {
        const { text, taggedUserAnchorEl } = this.state;

        // Prevent further input if the text exceeds the maximum length
        if (text.length >= MAX_COMMENT_TEXT_LENGTH && !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(event.key)) {
            event.preventDefault();
        }

        if (!taggedUserAnchorEl && event.which === Key.ENTER && !event.shiftKey) {
            event.preventDefault();
            this.submit();
        }
    }

    handleBlur = () => {
        const { submitVariant, onEmptyBlur } = this.props;

        this.setState({ isFocused: false });

        // "Cancel removed" behavior: blurring the send-icon composer while empty cancels
        // (the parent decides what that means - close the reply box, etc.)
        if (submitVariant !== "buttons" && isCommentTextEmpty(this.state.text)) {
            onEmptyBlur && onEmptyBlur();
        }
    }

    submit = async () => {
        const { text, isAssignUserToSlideSelected, isAssignSlideToMeSelected, isSubmitting } = this.state;
        const { onSubmit, showAssignToSlide, clearAfterSubmit } = this.props;

        // Ignore repeat submits (Enter-spam / double-click) while a save is in flight
        if (isSubmitting || isCommentTextEmpty(text)) {
            return;
        }

        const taggedUsers = this.getTaggedUsers();

        if (showAssignToSlide && isAssignUserToSlideSelected) {
            if (!await this.assignUserToSlide(taggedUsers[0])) {
                return;
            }
        } else if (showAssignToSlide && isAssignSlideToMeSelected) {
            if (!await this.assignUserToSlide(this.getMeUser())) {
                return;
            }
        }

        const taggedUsersFirebaseObject = {};
        const pendingTaggedUsersFirebaseObject = {};
        taggedUsers.forEach(user => {
            if (user.isPending) {
                pendingTaggedUsersFirebaseObject[stringToHex(user.email)] = true;
            } else {
                taggedUsersFirebaseObject[user.uid] = true;
            }
        });

        // Show the in-progress spinner and wait for the save to complete before clearing/closing,
        // so a slow or failing request doesn't silently discard the user's draft
        this.setState({ isSubmitting: true });
        try {
            await onSubmit(text, taggedUsersFirebaseObject, pendingTaggedUsersFirebaseObject);
        } catch (err) {
            // The parent logs the error; keep the draft and let the user retry
            logger.error(err, "[CommentComposer] submit() failed");
            if (this._isMounted) {
                this.setState({ isSubmitting: false });
            }
            return;
        }

        if (taggedUsers.length) {
            const props = {
                ...this.collabEventProps(),
                slide_id: ds.selection.slide.id,
                emails: taggedUsers.map(user => user.email),
                user_ids: taggedUsers.map(user => user.uid || null)
            };
            trackActivity("Collab", "Mention", null, null, props, { audit: true });
        }

        // The parent may unmount the composer on success (e.g. closing an edit box)
        if (!this._isMounted) {
            return;
        }

        if (clearAfterSubmit) {
            this.setState({ text: "", isAssignUserToSlideSelected: false, isAssignSlideToMeSelected: false, isSubmitting: false }, () => {
                this.textRef.current?.focus();
            });
        } else {
            this.setState({ isSubmitting: false });
        }
    }

    cancel = () => {
        const { onCancel } = this.props;
        onCancel && onCancel();
    }

    render() {
        const {
            byViewer,
            presentation,
            currentSlide,
            canEdit,
            assignedUser,
            showAssignToSlide,
            placeholder,
            submitVariant,
            submitLabel
        } = this.props;
        const {
            text,
            search,
            taggedUserAnchorEl,
            isAssignUserToSlideSelected,
            isAssignSlideToMeSelected,
            isShowingDialog,
            isFocused,
            isSubmitting
        } = this.state;

        const taggedUsers = this.getTaggedUsers();
        const assignToUser = taggedUsers[0];
        const isEmpty = isCommentTextEmpty(text);

        const showAssignCheck = (
            showAssignToSlide &&
            assignToUser &&
            (
                (
                    assignToUser.uid &&
                    assignedUser?.uid !== assignToUser.uid
                ) ||
                (
                    assignToUser.email &&
                    assignedUser?.email !== assignToUser.email
                )
            )
        );

        // "Assign slide to me" is offered once the comment tags someone but we're not already
        // offering to assign that person (e.g. they're the assignee already). Assignment is only
        // ever proposed alongside a mention, so an untagged draft shows no assign row at all.
        // Kept out of the player (byViewer), where slide assignment isn't part of the flow.
        const showAssignMe = (
            showAssignToSlide &&
            !byViewer &&
            !!assignToUser &&
            !showAssignCheck &&
            assignedUser?.uid !== app.user.id
        );

        return (
            <ComposerBox $isViewer={byViewer} $isFocused={isFocused || !isEmpty}>
                <ComposerInputArea>
                    <EditContentEditable
                        $isViewer={byViewer}
                        data-placeholder={placeholder}
                        onFocus={() => {
                            PresentationEditorController.selectionLayerController?.setSelectedElements([]);
                            this.setState({ isFocused: true });
                        }}
                        onBlur={this.handleBlur}
                        className="commentText"
                        innerRef={this.textRef}
                        html={text}
                        onKeyDown={this.handleKeyDown}
                        onChange={this.handleTextChange}
                        disabled={!canEdit}
                    />
                </ComposerInputArea>
                <AssignCollaboratorOrTeammateDropdown
                    sourceName="Comment"
                    anchorEl={taggedUserAnchorEl}
                    presentation={presentation}
                    slide={currentSlide}
                    filter={search}
                    showAssignedUser
                    onSelect={this.handleTagSelect}
                    onAddCollaboratorClick={this.handleAddCollaborator}
                    onClose={() => {
                        if (!isShowingDialog) this.closeCollaboratorDropdown();
                    }}
                />
                {
                    showAssignCheck &&
                    <>
                        <AssignToSlideDivider />
                        <AssignToSlideContainer>
                            <AssignToSlideCheckbox
                                color="primary"
                                checked={isAssignUserToSlideSelected}
                                onClick={this.handleClickAssignUser}
                            />
                            <CommentFormLabel>
                                Assign slide to {assignToUser.uid === app.user.id ? "me" : (assignToUser.displayName || assignToUser.email)}
                            </CommentFormLabel>
                        </AssignToSlideContainer>
                    </>
                }
                {
                    showAssignMe &&
                    <>
                        <AssignToSlideDivider />
                        <AssignToSlideContainer>
                            <AssignToSlideCheckbox
                                data-test-id="assign-slide-to-me"
                                color="primary"
                                checked={isAssignSlideToMeSelected}
                                onClick={() => this.setState(state => ({ isAssignSlideToMeSelected: !state.isAssignSlideToMeSelected }))}
                            />
                            <CommentFormLabel>
                                Assign slide to me
                            </CommentFormLabel>
                        </AssignToSlideContainer>
                    </>
                }
                {
                    submitVariant === "buttons" ? (
                        <ComposerActions>
                            <CommentButton
                                $isViewer={byViewer}
                                variant="text"
                                size="small"
                                disabled={isSubmitting}
                                onClick={this.cancel}
                            >Cancel</CommentButton>
                            <PostButton
                                $isViewer={byViewer}
                                variant="text"
                                size="small"
                                disabled={isEmpty || isSubmitting}
                                onClick={this.submit}
                            >{isSubmitting ? <CircularProgress size={16} color="inherit" /> : submitLabel}</PostButton>
                        </ComposerActions>
                    ) : (
                        !isEmpty &&
                        <SendRow>
                            {
                                isSubmitting ? (
                                    <CircularProgress size={20} color="inherit" style={{ color: "#11A9E2" }} />
                                ) : (
                                    <Tooltip title="Send" enterDelay={700} arrow>
                                        <SendButton data-test-id="send-comment" onClick={this.submit}>send</SendButton>
                                    </Tooltip>
                                )
                            }
                        </SendRow>
                    )
                }
            </ComposerBox>
        );
    }
}

export default CommentComposer;
